define('salesInboxBarometer',[
  'underscore'
], function (_) {
  'use strict';

  // ============================================================
  // SALES INBOX — per-locale queue barometer (shared).
  //
  // Given a `salesInbox:stats` response, builds one cell per locale showing
  // whether that queue is being cleared (resolved ≥ inflow) or falling behind,
  // weighted by the live open backlog and its staleness. Used by both the stats
  // "Pulse" screen and the queue list screen.
  //
  // Inputs consumed off the stats payload:
  //   byLocale          — items received in the window (inflow), per locale
  //   resolvedByLocale  — items resolved in the window, per locale
  //   openByLocale      — currently-open items (live backlog), per locale
  //   oldestOpenByLocale— age (minutes) of the oldest open item, per locale
  // ============================================================

  var STALE_MIN = 1440; // 24h — an open item older than this counts as stale

  function isNil(x) { return x === null || x === undefined; }
  function notNil(x) { return x !== null && x !== undefined; }

  function humanizeMinutes(min) {
    if (isNil(min)) { return null; }
    if (min < 60) { return min + 'm'; }
    var h = Math.floor(min / 60);
    if (h < 24) {
      var rm = min % 60;
      return rm ? h + 'h' + (rm < 10 ? '0' : '') + rm : h + 'h';
    }
    var d = Math.floor(h / 24);
    var rh = h % 24;
    return rh ? d + 'd ' + rh + 'h' : d + 'd';
  }

  // Build the barometer cells (sorted most-critical first) from a stats payload.
  function build(d) {
    d = d || {};
    var inflowBy = d.byLocale || {};
    var resolvedBy = d.resolvedByLocale || {};
    var openBy = d.openByLocale || {};
    var oldestBy = d.oldestOpenByLocale || {};

    var cells = [];
    var codes = _.union(_.keys(inflowBy), _.keys(resolvedBy), _.keys(openBy));
    _.each(codes, function (code) {
      var inflow = inflowBy[code] || 0;
      var resolved = resolvedBy[code] || 0;
      var open = openBy[code] || 0;
      if (!inflow && !resolved && !open) { return; }
      var oldestMin = notNil(oldestBy[code]) ? oldestBy[code] : null;
      var stale = notNil(oldestMin) && oldestMin > STALE_MIN;
      var net = resolved - inflow;
      // Clearance rate: 100% = breaking even; the bar fills toward break-even.
      var clearance = inflow > 0 ? (resolved / inflow) : 1.5;
      // Health folds flow, backlog and staleness together.
      var cls = net < 0 ? 'behind' : (stale ? 'warn' : (net > 0 ? 'ahead' : 'even'));
      // Ordering severity: behind first, then stale, then big/old backlogs.
      var severity = (net < 0 ? 1000000 + Math.abs(net) : 0) +
        (stale ? 100000 : 0) +
        (open * 100) +
        Math.round((oldestMin || 0) / 60);
      cells.push({
        label: code.toUpperCase(),
        inflow: inflow,
        resolved: resolved,
        open: open,
        oldest: notNil(oldestMin) ? humanizeMinutes(oldestMin) : null,
        stale: stale,
        net: net,
        cls: cls,
        severity: severity,
        netLabel: net > 0 ? ('▲ ' + net + ' ahead') : (net < 0 ? ('▼ ' + Math.abs(net) + ' behind') : 'even'),
        w: Math.max(4, Math.min(100, Math.round(clearance * 100)))
      });
    });
    cells.sort(function (a, b) { return b.severity - a.severity; });
    return cells;
  }

  // Render the barometer section markup (same structure as the stats template),
  // or '' when there's nothing to show.
  function html(cells) {
    if (!cells || !cells.length) { return ''; }
    var rows = _.map(cells, function (b) {
      var age = b.oldest ?
        (' · <span class="baro-age ' + (b.stale ? 'stale' : '') + '">oldest ' + _.escape(b.oldest) + '</span>') :
        '';
      return '<div class="baro-cell ' + b.cls + '">' +
        '<div class="baro-top"><span class="baro-loc">' + _.escape(b.label) + '</span>' +
        '<span class="baro-net">' + _.escape(b.netLabel) + '</span></div>' +
        '<div class="baro-open"><b>' + b.open + '</b> open' + age + '</div>' +
        '<div class="baro-track"><div class="baro-fill" style="width:' + b.w + '%"></div></div>' +
        '<div class="baro-foot">' + b.resolved + ' resolved · ' + b.inflow + ' in</div>' +
        '</div>';
    }).join('');
    return '<div class="section barometer">' +
      '<div class="section-head"><span class="section-label">Queue barometer · by locale</span>' +
      '<span class="section-note">are we clearing the queue this period?</span></div>' +
      '<div class="baro-grid">' + rows + '</div>' +
      '</div>';
  }

  return { build: build, html: html };
});

